import type { Space } from "../api/spaces";

export type SpacesPage = {
    spaces: Space[];
    total: number;
    page: number;
    hasMore: boolean;
};

type SpacesPageLoader = (page: number, limit: number) => Promise<SpacesPage>;

export async function loadAllJoinedSpaces(
    loadPage: SpacesPageLoader,
    limit = 100
): Promise<{ spaces: Space[]; total: number }> {
    const spacesById = new Map<string, Space>();
    let page = 1;
    let total = 0;
    let hasMore = true;

    while (hasMore) {
        const result = await loadPage(page, limit);
        total = Math.max(total, result.total);

        result.spaces.forEach((space) => {
            spacesById.set(space.id, space);
        });

        if (!result.hasMore || result.spaces.length === 0) {
            hasMore = false;
        } else {
            page += 1;
        }
    }

    const spaces = Array.from(spacesById.values());
    return { spaces, total: Math.max(total, spaces.length) };
}

function parseSpaceCount(value: string | number | undefined): number {
    const raw = String(value || "0").trim().toLowerCase().replace(/,/g, "");
    if (raw.endsWith("k")) {
        const number = Number(raw.slice(0, -1));
        return Number.isFinite(number) ? number * 1000 : 0;
    }
    const number = Number(raw);
    return Number.isFinite(number) ? number : 0;
}

export function filterJoinedSpaces(spaces: Space[], activeFilter: string, searchQuery: string): Space[] {
    const query = searchQuery.trim().toLowerCase();

    return spaces.filter((space) => {
        const matchesFilter =
            activeFilter === "All" ||
            (activeFilter === "New" && space.isNew) ||
            (activeFilter === "Popular" && parseSpaceCount(space.members) >= 3000) ||
            space.category === activeFilter;

        const haystack = [space.title, space.desc, space.category, space.by, ...(space.tags || [])]
            .join(" ")
            .toLowerCase();

        return matchesFilter && (!query || haystack.includes(query));
    });
}

export function buildJoinedSpacesView(filteredSpaces: Space[], featuredLimit = 9) {
    const defaultSpace = filteredSpaces.find((space) => space.id === "blackspace");
    const featuredSpaces = filteredSpaces
        .filter((space) => space.id !== "blackspace")
        .slice(0, featuredLimit);
    const visibleCardsCount = featuredSpaces.length + (defaultSpace ? 1 : 0);

    return {
        defaultSpace,
        featuredSpaces,
        visibleCardsCount,
        remainingCount: Math.max(0, filteredSpaces.length - visibleCardsCount),
    };
}

